import React, { useRef, useEffect, useLayoutEffect, useMemo, memo, useState } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import {
    LineChart,
    BarChart,
    PieChart,
    ScatterChart,
    FunnelChart,
    GaugeChart,
    TreemapChart,
} from 'echarts/charts';
import {
    GridComponent,
    TooltipComponent,
    LegendComponent,
    DataZoomComponent,
    ToolboxComponent,
    TitleComponent,
    MarkLineComponent,
    MarkPointComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// Регистрируем только нужные компоненты для tree-shaking
echarts.use([
    LineChart,
    BarChart,
    PieChart,
    ScatterChart,
    FunnelChart,
    GaugeChart,
    TreemapChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    DataZoomComponent,
    ToolboxComponent,
    TitleComponent,
    MarkLineComponent,
    MarkPointComponent,
    CanvasRenderer,
]);

/**
 * LTTB (Largest Triangle Three Buckets) алгоритм для downsampling данных
 * Сохраняет визуальную форму графика при уменьшении количества точек
 */
function downsampleLTTB<T extends { x: string | number; y: number }>(
    data: T[],
    threshold: number
): T[] {
    if (data.length <= threshold || threshold < 3) {
        return data;
    }

    const sampled: T[] = [];
    const bucketSize = (data.length - 2) / (threshold - 2);

    let a = 0;
    sampled.push(data[a]); // Всегда добавляем первую точку

    for (let i = 0; i < threshold - 2; i++) {
        // Вычисляем среднюю точку следующего bucket
        const avgRangeStart = Math.floor((i + 1) * bucketSize) + 1;
        const avgRangeEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, data.length);

        let avgX = 0;
        let avgY = 0;
        const avgRangeLength = avgRangeEnd - avgRangeStart;

        for (let j = avgRangeStart; j < avgRangeEnd; j++) {
            avgX += typeof data[j].x === 'number' ? (data[j].x as number) : j;
            avgY += data[j].y;
        }
        avgX /= avgRangeLength;
        avgY /= avgRangeLength;

        // Находим точку в текущем bucket с максимальной площадью треугольника
        const rangeStart = Math.floor(i * bucketSize) + 1;
        const rangeEnd = Math.floor((i + 1) * bucketSize) + 1;

        const pointAX: number = typeof data[a].x === 'number' ? (data[a].x as number) : a;
        const pointAY = data[a].y;

        let maxArea = -1;
        let maxAreaPoint = rangeStart;

        for (let j = rangeStart; j < rangeEnd; j++) {
            const pointX: number = typeof data[j].x === 'number' ? (data[j].x as number) : j;
            const pointY = data[j].y;

            // Площадь треугольника
            const area = Math.abs(
                (pointAX - avgX) * (pointY - pointAY) -
                (pointAX - pointX) * (avgY - pointAY)
            ) * 0.5;

            if (area > maxArea) {
                maxArea = area;
                maxAreaPoint = j;
            }
        }

        sampled.push(data[maxAreaPoint]);
        a = maxAreaPoint;
    }

    sampled.push(data[data.length - 1]); // Всегда добавляем последнюю точку

    return sampled;
}

/**
 * Простой downsampling для данных без x координаты
 */
function downsampleSimple<T>(data: T[], threshold: number): T[] {
    if (data.length <= threshold) {
        return data;
    }

    const sampled: T[] = [];
    const step = data.length / threshold;

    for (let i = 0; i < threshold; i++) {
        const index = Math.floor(i * step);
        sampled.push(data[index]);
    }

    // Убедимся, что последний элемент включён
    if (sampled[sampled.length - 1] !== data[data.length - 1]) {
        sampled[sampled.length - 1] = data[data.length - 1];
    }

    return sampled;
}

export interface EChartProps {
    option: echarts.EChartsCoreOption;
    style?: React.CSSProperties;
    className?: string;
    loading?: boolean;
    theme?: string | object;
    // Оптимизации
    lazyLoad?: boolean; // Ленивая загрузка (рендерить только когда видим)
    maxDataPoints?: number; // Максимум точек (по умолчанию 150)
    notMerge?: boolean;
    lazyUpdate?: boolean;
    onChartReady?: (chart: any) => void;
    onEvents?: Record<string, (params: any) => void>;
}

/**
 * Оптимизированный компонент графика на ECharts
 * 
 * Особенности:
 * - Canvas рендеринг (быстрее SVG в 10-50 раз)
 * - Автоматический downsampling данных (LTTB алгоритм)
 * - Lazy loading через Intersection Observer
 * - Tree-shaking для минимального размера бандла
 */
const EChart: React.FC<EChartProps> = memo(({
    option,
    style,
    className,
    loading = false,
    theme,
    lazyLoad = true,
    maxDataPoints = 150,
    notMerge = true, // По умолчанию true, чтобы серии полностью заменялись при обновлении
    lazyUpdate = true,
    onChartReady,
    onEvents,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<ReactEChartsCore>(null);
    const chartInstanceRef = useRef<any>(null);
    const [isVisible, setIsVisible] = useState(!lazyLoad);

    // Intersection Observer для lazy loading
    useEffect(() => {
        if (!lazyLoad || !containerRef.current) {
            setIsVisible(true);
            return;
        }

        // Проверка поддержки IntersectionObserver для старых браузеров (включая Яндекс Браузер)
        if (typeof IntersectionObserver === 'undefined') {
            // Fallback: сразу показываем график без lazy loading
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '100px', // Начинаем загрузку за 100px до появления
                threshold: 0,
            }
        );

        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, [lazyLoad]);

    // Оптимизируем данные через downsampling
    const optimizedOption = useMemo(() => {
        if (!option || !option.series) {
            return option;
        }

        const series = Array.isArray(option.series) ? option.series : [option.series];
        
        const optimizedSeries = series.map((s: any) => {
            if (!s.data || !Array.isArray(s.data)) {
                return s;
            }

            // Применяем downsampling только для линейных графиков с большим количеством данных
            // maxDataPoints = 0 отключает downsampling
            if (maxDataPoints > 0 && (s.type === 'line' || s.type === 'scatter') && s.data.length > maxDataPoints) {
                // Проверяем формат данных
                if (s.data[0] && typeof s.data[0] === 'object' && 'x' in s.data[0] && 'y' in s.data[0]) {
                    return {
                        ...s,
                        data: downsampleLTTB(s.data, maxDataPoints),
                    };
                } else if (s.data[0] && Array.isArray(s.data[0])) {
                    // Формат [[x, y], [x, y], ...]
                    const converted = s.data.map((d: [number | string, number]) => ({ x: d[0], y: d[1] }));
                    const downsampled = downsampleLTTB(converted, maxDataPoints);
                    return {
                        ...s,
                        data: downsampled.map((d) => [d.x, d.y]),
                    };
                } else {
                    // Простой массив значений
                    return {
                        ...s,
                        data: downsampleSimple(s.data, maxDataPoints),
                    };
                }
            }

            return s;
        });

        return {
            ...option,
            series: optimizedSeries,
            // Глобальные оптимизации
            animation: option.animation !== undefined ? option.animation : false,
            progressive: 500, // Прогрессивный рендеринг для больших данных
            progressiveThreshold: 1000,
        };
    }, [option, maxDataPoints]);

    // Очистка чарта перед применением новой опции (через echarts-for-react).
    // useLayoutEffect cleanup выполняется в mutation-фазе React commit — ДО
    // componentDidUpdate дочернего ReactEChartsCore, который вызывает setOption.
    // Это предотвращает race condition: mousemove/mouseout на старых данных серии
    // после их замены через notMerge=true (ошибка __ec_inner_XX в PieSeriesModel).
    useLayoutEffect(() => {
        return () => {
            const chart = chartInstanceRef.current;
            if (chart && !chart.isDisposed()) {
                chart.clear();
            }
        };
    }, [optimizedOption]);

    // Обработчик готовности графика
    const handleChartReady = (chart: any) => {
        chartInstanceRef.current = chart;
        onChartReady?.(chart);
    };

    const defaultStyle: React.CSSProperties = {
        width: '100%',
        height: '400px',
        ...style,
    };

    // Placeholder пока график не виден
    if (!isVisible) {
        return (
            <div
                ref={containerRef}
                className={className}
                style={{
                    ...defaultStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px',
                }}
            >
                <span style={{ color: '#999' }}>Загрузка графика...</span>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={className}>
            <ReactEChartsCore
                ref={chartRef}
                echarts={echarts}
                option={optimizedOption}
                style={defaultStyle}
                theme={theme}
                notMerge={notMerge}
                lazyUpdate={lazyUpdate}
                showLoading={loading}
                onChartReady={handleChartReady}
                onEvents={onEvents}
                opts={{
                    renderer: 'canvas', // Canvas быстрее SVG
                    // Проверка devicePixelRatio для совместимости с разными браузерами
                    devicePixelRatio: typeof window !== 'undefined' && window.devicePixelRatio 
                        ? (window.devicePixelRatio > 2 ? 2 : window.devicePixelRatio) 
                        : 1, // Ограничиваем для производительности
                }}
            />
        </div>
    );
});

EChart.displayName = 'EChart';

export default EChart;

// Экспортируем утилиты для использования в других компонентах
export { downsampleLTTB, downsampleSimple };

// Экспортируем echarts для кастомных настроек
export { echarts };
